import { FormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Revisione53State } from '../../../store/revisione53.reducers';
import { getControl, getGroup } from '@shared/helpers/form.helper';
import { formPaths } from '@shared/constants/form-paths.const';
import { debounceTime, merge, startWith } from 'rxjs';
import { Utilities } from '@core/helpers/utilities';
import { updateVerbaleOnStore } from '@core/helpers/create-validator.helper';
import { MyVerbale } from '../../../models/interfaces/revisione.interface';
import { cloneDeep } from 'lodash-es';
import { nz } from '@shared/helpers/utils.helper';
import { updateWarningGHISuperioriB, updateWarningGreaterThanField } from '../validators/helpers/dati-bilancio-form.helpers';
import { withInitialValueDebounced } from '@shared/operators/with-initial-value-debounced.operator';

export const datiBilancioRistorniCalcoliListeners = (form: FormGroup, store: Store<Revisione53State>): void => {
    const flags = [false, true];
    flags.forEach((flag) => {
        my_validascheda2(form, store, flag);
        my_validaVerificaDelibere(form, store, flag);
        my_valida30PercentoRistorno(form, store, flag);
        my_AvanzoDiGestioneRigo23(form, store, flag);
    });
};

const my_validascheda2 = (form: FormGroup, store: Store<Revisione53State>, isFirstYear: boolean): void => {
    const buildName = (name: string) => `${name}${isFirstYear ? 'Anno1' : ''}`;

    const schedaRistorniGroup = getGroup(form, `${formPaths.bilancio}.my_SchedaRistorni`);
    const my_validascheda2Control = getControl(schedaRistorniGroup, buildName('my_validascheda2'));
    const my_CostoPersCoopLavControl = getControl(schedaRistorniGroup, buildName('my_CostoPersCoopLav'));
    const my_RistornoCoopLavControl = getControl(schedaRistorniGroup, buildName('my_RistornoCoopLav'));
    const my_RistornoCoopUtControl = getControl(schedaRistorniGroup, buildName('my_RistornoCoopUt'));
    const my_RistornoCoopApControl = getControl(schedaRistorniGroup, buildName('my_RistornoCoopAp'));

    merge(
        my_CostoPersCoopLavControl.valueChanges.pipe(startWith(my_CostoPersCoopLavControl.value)),
        my_RistornoCoopLavControl.valueChanges.pipe(startWith(my_RistornoCoopLavControl.value)),
        my_RistornoCoopUtControl.valueChanges.pipe(startWith(my_RistornoCoopUtControl.value)),
        my_RistornoCoopApControl.valueChanges.pipe(startWith(my_RistornoCoopApControl.value))
    )
        .pipe(debounceTime(Utilities.LISTENERS_DEBOUNCE_TIME))
        .subscribe(() => {
            updateVerbaleOnStore(store, (myVerbale) => {
                const myVerbaleClone: MyVerbale = cloneDeep(myVerbale);
                const my_CostoPersCoopLav: string = my_CostoPersCoopLavControl.value;
                const my_RistornoCoopLav: string = my_RistornoCoopLavControl.value;
                const my_RistornoCoopUt: string = my_RistornoCoopUtControl.value;
                const my_RistornoCoopAp: string = my_RistornoCoopApControl.value;
                const targetValue = (
                    (my_CostoPersCoopLav?.length ?? 0) * (my_RistornoCoopLav?.length ?? 0) +
                    (my_RistornoCoopUt?.length ?? 0) +
                    (my_RistornoCoopAp?.length ?? 0)
                ).toString(10);
                my_validascheda2Control.patchValue(targetValue, { emitEvent: false });
                myVerbaleClone.my_SezioneI__Rilevazione.my_Bilancio.my_SchedaRistorni[buildName('my_validascheda2')] = targetValue;
                return myVerbaleClone;
            });
        });
};

/** Computes validaVerificaDelibere = round((Nz(Liquidazioni) + Nz(AumentoGratuito) + Nz(DistribuzioneGratuita)) * 100) / 100. */
const my_validaVerificaDelibere = (form: FormGroup, store: Store<Revisione53State>, isFirstYear: boolean): void => {
    const suffix = isFirstYear ? 'Anno1' : '';
    const schedaRistorniGroup = getGroup(form, `${formPaths.bilancio}.my_SchedaRistorni`);
    const liquidazioniControl = getControl(schedaRistorniGroup, `my_ImportoLiquidazioniSalariali${suffix}`);
    const aumentoControl = getControl(schedaRistorniGroup, `my_ImportoAumentoGratuito${suffix}`);
    const distribuzioneControl = getControl(schedaRistorniGroup, `my_ImportoDistribuzioneGratuita${suffix}`);
    const targetField = `validaVerificaDelibere${suffix}`;
    const targetControl = getControl(schedaRistorniGroup, targetField);

    merge(
        liquidazioniControl.valueChanges.pipe(startWith(liquidazioniControl.value)),
        aumentoControl.valueChanges.pipe(startWith(aumentoControl.value)),
        distribuzioneControl.valueChanges.pipe(startWith(distribuzioneControl.value))
    )
        .pipe(debounceTime(Utilities.LISTENERS_DEBOUNCE_TIME))
        .subscribe(() => {
            const value = (Math.round((nz(liquidazioniControl.value) + nz(aumentoControl.value) + nz(distribuzioneControl.value)) * 100) / 100).toString(10);
            targetControl.patchValue(value, { emitEvent: false });

            updateVerbaleOnStore(store, (myVerbale) => {
                const myVerbaleClone: MyVerbale = cloneDeep(myVerbale);

                myVerbaleClone.my_SezioneI__Rilevazione.my_Bilancio.my_SchedaRistorni[targetField] = value;

                return myVerbaleClone;
            });

            if (isFirstYear) {
                updateWarningGHISuperioriB(form, store, 'my_AvanzoDiGestionePrevalenzaAnno1', 'validaVerificaDelibereAnno1', 'my_CkGHISuperioriBAnno1');
            } else {
                updateWarningGHISuperioriB(form, store, 'my_AvanzoDiGestionePrevalenza', 'validaVerificaDelibere', 'my_CkGHISuperioriB');
            }
        });
};

const my_valida30PercentoRistorno = (form: FormGroup, store: Store<Revisione53State>, isFirstYear: boolean): void => {
    const suffix = isFirstYear ? 'Anno1' : '';
    const schedaRistorniGroup = getGroup(form, `${formPaths.bilancio}.my_SchedaRistorni`);
    const costoControl = getControl(schedaRistorniGroup, `my_CostoPersCoopLav${suffix}`);
    const targetField = `my_valida30PercentoRistorno${suffix}`;
    const targetControl = getControl(schedaRistorniGroup, targetField);

    costoControl.valueChanges.pipe(withInitialValueDebounced(costoControl)).subscribe((value: string) => {
        const result = (nz(value) * 0.3).toString(10);

        targetControl.patchValue(result, { emitEvent: false });

        updateVerbaleOnStore(store, (myVerbale) => {
            const myVerbaleClone: MyVerbale = cloneDeep(myVerbale);

            myVerbaleClone.my_SezioneI__Rilevazione.my_Bilancio.my_SchedaRistorni[targetField] = result;

            return myVerbaleClone;
        });

        if (isFirstYear) {
            updateWarningGreaterThanField(
                form,
                store,
                'my_CkApplicaRistorniAnno1',
                'my_RistornoCoopLavAnno1',
                'my_valida30PercentoRistornoAnno1',
                'my_CkDSuperiore30PerCentoCAnno1'
            );
        } else {
            updateWarningGreaterThanField(form, store, 'my_CkApplicatiRistorni', 'my_RistornoCoopLav', 'my_valida30PercentoRistorno', 'my_CkDSuperiore30PerCentoC');
        }
    });
};

/** Computes my_AvanzoDiGestioneRigo23 = Nz(Rigo23ContoEc) + Nz(RistorniContoEc) - Nz(EventualeDContoEc) - Nz(EventualeEContoEc). */
const my_AvanzoDiGestioneRigo23 = (form: FormGroup, store: Store<Revisione53State>, isFirstYear: boolean): void => {
    const suffix = isFirstYear ? 'Anno1' : '';
    const schedaRistorniGroup = getGroup(form, `${formPaths.bilancio}.my_SchedaRistorni`);
    const rigo23Control = getControl(schedaRistorniGroup, `my_Rigo23ContoEc${suffix}`);
    const ristorniControl = getControl(schedaRistorniGroup, `my_RistorniContoEc${suffix}`);
    const eventualeDControl = getControl(schedaRistorniGroup, `my_EventualeDContoEc${suffix}`);
    const eventualeEControl = getControl(schedaRistorniGroup, `my_EventualeEContoEc${suffix}`);
    const targetField = `my_AvanzoDiGestioneRigo23${suffix}`;
    const targetControl = getControl(schedaRistorniGroup, targetField);

    merge(
        rigo23Control.valueChanges.pipe(startWith(rigo23Control.value)),
        ristorniControl.valueChanges.pipe(startWith(ristorniControl.value)),
        eventualeDControl.valueChanges.pipe(startWith(eventualeDControl.value)),
        eventualeEControl.valueChanges.pipe(startWith(eventualeEControl.value))
    )
        .pipe(debounceTime(Utilities.LISTENERS_DEBOUNCE_TIME))
        .subscribe(() => {
            const result = (nz(rigo23Control.value) + nz(ristorniControl.value) - nz(eventualeDControl.value) - nz(eventualeEControl.value)).toString(10);
            targetControl.patchValue(result, { emitEvent: false });
            updateVerbaleOnStore(store, (myVerbale) => {
                const myVerbaleClone: MyVerbale = cloneDeep(myVerbale);
                myVerbaleClone.my_SezioneI__Rilevazione.my_Bilancio.my_SchedaRistorni[targetField] = result;
                return myVerbaleClone;
            });
        });
};
